<template>
  <div
    class="overview-kpi-tile flex flex-col gap-3 min-h-[7.5rem]"
    data-testid="overview-kpi-constraints-diff"
  >
    <span class="text-[13px] text-[var(--color-disabled)]">
      {{ $t('UserProjects.overview.kpi.constraintsDiff.label') }}
    </span>

    <div v-if="summaryLoading" class="text-sm text-[var(--color-disabled)]">
      {{ $t('UserProjects.overview.loading') }}
    </div>

    <div v-else-if="summaryError" class="text-sm text-[var(--color-error-text)]">
      {{ summaryError }}
    </div>

    <template v-else>
      <div class="grid grid-cols-2 gap-3">
        <div
          class="overview-constraints-diff__bucket overview-constraints-diff__bucket--intern"
          data-testid="overview-kpi-constraints-intern"
        >
          <span class="overview-constraints-diff__bucket-label">
            {{ $t('UserProjects.overview.kpi.constraintsDiff.intern') }}
          </span>
          <span class="overview-constraints-diff__count">
            {{ $t('UserProjects.overview.kpi.constraintsDiff.openCount', { count: internOpen }) }}
          </span>
          <span class="overview-constraints-diff__avg">
            {{ avgDaysLabel(internAvgDays) }}
          </span>
        </div>

        <div
          class="overview-constraints-diff__bucket overview-constraints-diff__bucket--cross"
          data-testid="overview-kpi-constraints-cross"
        >
          <div class="overview-constraints-diff__bucket-label-row">
            <span class="overview-constraints-diff__bucket-label overview-constraints-diff__bucket-label--cross">
              {{ $t('UserProjects.overview.kpi.constraintsDiff.cross') }}
            </span>
            <i
              v-if="crossSlower"
              class="pi pi-exclamation-triangle text-xs text-[var(--color-warning-text)] shrink-0"
              aria-hidden="true"
            />
          </div>
          <span class="overview-constraints-diff__count">
            {{ $t('UserProjects.overview.kpi.constraintsDiff.openCount', { count: crossOpen }) }}
          </span>
          <span class="overview-constraints-diff__avg">
            {{ avgDaysLabel(crossAvgDays) }}
            <span v-if="crossSlower" aria-hidden="true"> ⚠</span>
          </span>
        </div>
      </div>

      <p
        v-if="crossSlower"
        class="overview-constraints-diff__warning text-xs text-[var(--color-warning-text)] m-0"
        data-testid="overview-constraints-diff-warning"
      >
        {{ $t('UserProjects.overview.kpi.constraintsDiff.crossSlowerWarning') }}
      </p>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'OverviewConstraintsDiffTile',
  computed: {
    ...mapGetters('pmConstraint', {
      summary: 'summary',
      summaryLoading: 'summaryLoading',
      summaryError: 'summaryError',
    }),
    internOpen() {
      return this.summary?.intern?.openCount ?? 0;
    },
    crossOpen() {
      return this.summary?.cross?.openCount ?? 0;
    },
    internAvgDays() {
      return this.summary?.intern?.avgDaysOpen ?? null;
    },
    crossAvgDays() {
      return this.summary?.cross?.avgDaysOpen ?? null;
    },
    crossSlower() {
      if (this.internAvgDays === null || this.crossAvgDays === null) return false;
      if (this.crossOpen === 0) return false;
      return this.crossAvgDays > this.internAvgDays;
    },
  },
  methods: {
    avgDaysLabel(value) {
      if (value === null || value === undefined) {
        return this.$t('UserProjects.overview.kpi.constraintsDiff.noAvg');
      }
      return this.$t('UserProjects.overview.kpi.constraintsDiff.avgDays', { days: value });
    },
  },
};
</script>

<style scoped>
.overview-kpi-tile {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
  padding: 1.25rem;
  min-width: 0;
}

.overview-constraints-diff__bucket {
  border-radius: 10px;
  padding: 0.75rem;
  min-width: 0;
}

.overview-constraints-diff__bucket-label-row {
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
  flex-wrap: wrap;
  min-width: 0;
}

.overview-constraints-diff__bucket-label {
  display: block;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: var(--color-info-text);
  overflow-wrap: anywhere;
  word-break: break-word;
  hyphens: auto;
  line-height: 1.25;
}

.overview-constraints-diff__bucket-label--cross {
  color: var(--color-orange);
  flex: 1 1 3rem;
  min-width: 0;
}

.overview-constraints-diff__count {
  display: block;
  font-size: clamp(1.125rem, 4vw, 1.5rem);
  font-weight: 700;
  line-height: 1.15;
  color: var(--color-dark);
  margin-top: 0.25rem;
  overflow-wrap: anywhere;
}

.overview-constraints-diff__avg {
  display: block;
  font-size: 12px;
  color: var(--color-disabled);
  margin-top: 0.25rem;
  overflow-wrap: anywhere;
  line-height: 1.35;
}

.overview-constraints-diff__bucket--intern {
  background: var(--color-constraint-internal-surface);
}

.overview-constraints-diff__bucket--cross {
  background: var(--color-constraint-cross-surface);
}

.overview-constraints-diff__warning {
  overflow-wrap: anywhere;
  line-height: 1.4;
}
</style>
